import { useCallback, useRef, useState, useEffect } from 'react';
import { useChatStore } from './store/chatStore';
import { ChatLayout } from './components/layout/ChatLayout';
import { Sidebar } from './components/layout/Sidebar';
import { SourcePanel } from './components/sources/SourcePanel';
import { RipgrepPanel } from './components/search/RipgrepPanel';
import { EvaluationReview } from './components/evaluation/EvaluationReview';
import { AssistantEvalDashboard } from './components/evaluation/AssistantEvalDashboard';
import DocProcessingPage from './components/doc-processing/DocProcessingPage';
import SchemaGraphPage from './components/schema-graph/SchemaGraphPage';
import { IssueAnalysisPage } from './components/issue-analysis/IssueAnalysisPage';
import { TicketChat } from './components/issue-analysis/TicketChat';
import { CrAnalysisPage } from './components/cr-analysis/CrAnalysisPage';
import SecurityPage from './components/admin/SecurityPage';
import { SystemAdministrationPage } from './components/admin/SystemAdministrationPage';
import { UsageStatsPage } from './components/admin/UsageStatsPage';
import { FrontendTypeScriptGraphPage } from './components/ts-graph/FrontendTypeScriptGraphPage';
import { GripVertical } from 'lucide-react';
import { AuthProvider } from './auth/AuthProvider';

// Simple hash-based routing
function useHashRoute() {
  const [route, setRoute] = useState(window.location.hash || '#/');

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(window.location.hash || '#/');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return route;
}

// Main app content (protected by AuthProvider)
function AppContent() {
  const route = useHashRoute();
  const { isSidebarOpen, showSourcePanel, showRipgrepPanel, sidebarWidth, setSidebarWidth, sidebarCollapsed, isLoading } = useChatStore();

  // Collapsed sidebar shows icon-only nav. 56 px fits a 16 px icon plus padding
  // and matches the common VS Code / Notion collapse width.
  const COLLAPSED_WIDTH = 56;
  const effectiveSidebarWidth = sidebarCollapsed ? COLLAPSED_WIDTH : sidebarWidth;
  const isResizing = useRef(false);
  const sidebarRef = useRef<HTMLElement>(null);

  const startResizing = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const resize = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!isResizing.current) return;
    const newWidth = e.clientX;
    setSidebarWidth(newWidth);
  }, [setSidebarWidth]);

  // Set up global mouse listeners for resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startResizing();

    const handleMouseMove = (e: MouseEvent) => resize(e);
    const handleMouseUp = () => {
      stopResizing();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [startResizing, stopResizing, resize]);

  const isChatRoute = !route || route === '#/' || route === '#';
  const goChat = useCallback(() => { window.location.hash = '#/'; }, []);

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar — always visible */}
      {isSidebarOpen && (
        <>
          <aside
            ref={sidebarRef}
            className="bg-gray-900 text-white flex-shrink-0 relative h-full overflow-hidden transition-[width] duration-150"
            style={{ width: effectiveSidebarWidth }}
          >
            <Sidebar />
          </aside>
          {/* Resize handle — hidden while collapsed since the width is fixed */}
          {!sidebarCollapsed && (
            <div
              onMouseDown={handleMouseDown}
              className="w-1 bg-gray-700 hover:bg-primary-500 cursor-col-resize flex-shrink-0 flex items-center justify-center group transition-colors"
              title="Drag to resize"
            >
              <GripVertical size={12} className="text-gray-500 group-hover:text-white" />
            </div>
          )}
        </>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 relative overflow-hidden">
        {/* Global query-in-progress indicator — shown on non-chat routes */}
        {!isChatRoute && isLoading && (
          <div
            onClick={goChat}
            className="absolute top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-primary-500 text-white rounded-full shadow-lg cursor-pointer hover:bg-primary-600 transition-colors"
            title="Click to return to chat"
          >
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-sm font-medium">Query in progress…</span>
          </div>
        )}

        {/* Route content */}
        {route === '#/doc-processing' && (
          <DocProcessingPage onBack={goChat} />
        )}
        {route === '#/graph-viz' && (
          <SchemaGraphPage onBack={goChat} />
        )}
        {route === '#/issue-analysis' && (
          <IssueAnalysisPage onBack={goChat} />
        )}
        {route === '#/ticket-chat' && (
          <div className="flex-1 min-h-0">
            <TicketChat />
          </div>
        )}
        {route === '#/cr-analysis' && (
          <CrAnalysisPage onBack={goChat} />
        )}
        {route === '#/admin/security' && (
          <SecurityPage />
        )}
        {route === '#/admin/system-config' && (
          <SystemAdministrationPage />
        )}
        {route === '#/admin/usage-stats' && (
          <UsageStatsPage />
        )}
        {route === '#/ts-graph' && (
          <FrontendTypeScriptGraphPage onBack={goChat} />
        )}
        {route === '#/evaluation' && (
          <div className="flex-1 overflow-y-auto">
            <EvaluationReview />
          </div>
        )}
        {route === '#/eval-results' && (
          <div className="flex-1 overflow-y-auto">
            <AssistantEvalDashboard />
          </div>
        )}

        {/* Main chat layout — always mounted so in-flight queries are never interrupted */}
        <div
          className="flex-1 flex min-w-0 overflow-hidden"
          style={{ display: isChatRoute ? undefined : 'none' }}
        >
          <main className="flex-1 flex flex-col min-w-0">
            <ChatLayout />
          </main>

          {/* Source panel (split view) */}
          {showSourcePanel && (
            <aside className="w-96 border-l border-gray-200 bg-white flex-shrink-0 h-full overflow-hidden">
              <SourcePanel />
            </aside>
          )}

          {/* Ripgrep search panel (right side) */}
          {showRipgrepPanel && (
            <aside className="w-[420px] border-l border-gray-800 flex-shrink-0 h-full overflow-hidden">
              <RipgrepPanel />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

// Root App with authentication
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
